import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosConfig from '../../../api/axiosConfig';
import SearchableSelect from '../../../components/SearchableSelect';
import * as XLSX from 'xlsx';


// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const GelenOdemeler = () => {
  const adminId = localStorage.getItem('adminId'); // Admin ID'yi al
  const adminRole = localStorage.getItem('adminRole');
  const isSuperAdmin = adminRole === 'superadmin' || adminId === 'superadmin-001';
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('thisMonth');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: '',
    end: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [bankFilter, setBankFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  
  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  // Stats
  const [stats, setStats] = useState({
    payment_count: 0,
    total_amount: 0,
    average_amount: 0,
    today_amount: 0,
    week_amount: 0,
    month_amount: 0
  });
  


  // Banka listesi
  const [bankList, setBankList] = useState([]);
  const yearOptions = useMemo(
    () => Array.from({ length: 6 }, (_, index) => new Date().getFullYear() - index),
    []
  );

  // Initial data - sadece bir kere yükle
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Sadece students, teachers ve lessons'ı yükle
      const [studentsRes, lessonsRes, teachersRes] = await Promise.all([
        axiosConfig.get('/admin/ogrencis'),
        axiosConfig.get('/admin/ogretmenders'),
        axiosConfig.get('/admin/teachers')
      ]);
      
      // Students data kontrolü - array olduğundan emin ol
      const studentsData = studentsRes.data;
      if (Array.isArray(studentsData)) {
        setStudents(studentsData);
      } else if (studentsData && Array.isArray(studentsData.data)) {
        setStudents(studentsData.data);
      } else if (studentsData && Array.isArray(studentsData.students)) {
        setStudents(studentsData.students);
      } else {
        console.warn('Students data is not an array:', studentsData);
        setStudents([]);
      }
      
      // Lessons data kontrolü
      const lessonsData = lessonsRes.data;
      if (Array.isArray(lessonsData)) {
        setLessons(lessonsData);
      } else if (lessonsData && Array.isArray(lessonsData.data)) {
        setLessons(lessonsData.data);
      } else {
        setLessons([]);
      }
      
      // Teachers data kontrolü
      const teachersData = teachersRes.data;
      if (Array.isArray(teachersData)) {
        setTeachers(teachersData);
      } else if (teachersData && Array.isArray(teachersData.data)) {
        setTeachers(teachersData.data);
      } else {
        setTeachers([]);
      }
    } catch (error) {
      console.error('Initial data yüklenemedi:', error);
      // Hata durumunda boş array'ler set et
      setStudents([]);
      setLessons([]);
      setTeachers([]);
    }
  };

  // Ödemeleri yükle - filters değiştiğinde çalışacak
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    
    try {
      const params = {
        period: selectedPeriod,
        year: selectedYear,
        month: selectedMonth,
        bank: bankFilter,
        search: debouncedSearchTerm
      };
      
      // Custom date range için
      if (selectedPeriod === 'custom' && selectedDateRange.start && selectedDateRange.end) {
        params.start_date = selectedDateRange.start;
        params.end_date = selectedDateRange.end;
      }
      
      const response = await axiosConfig.get('/admin/odemes', { params });
      
      if (response.data) {
        setPayments(response.data.data || []);
        setStats(response.data.stats || {});
        
        // Tüm banka listesini al
        const allBanks = response.data.banks || [];
        setBankList(allBanks);
      }
    } catch (error) {
      console.error('Ödemeler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedYear, selectedMonth, selectedDateRange, bankFilter, debouncedSearchTerm]);

  // Filtreler değiştiğinde ödemeleri yeniden yükle
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Sıralanmış ödemeler - sadece sıralama için useMemo
  const sortedPayments = useMemo(() => {
    const sorted = [...payments];
    
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.tarih) - new Date(a.tarih);
        case 'date_asc':
          return new Date(a.tarih) - new Date(b.tarih);
        case 'amount_desc':
          return (b.tutar || 0) - (a.tutar || 0);
        case 'amount_asc':
          return (a.tutar || 0) - (b.tutar || 0);
        case 'name_asc':
          return (a.student_name || '').localeCompare(b.student_name || '');
        case 'name_desc':
          return (b.student_name || '').localeCompare(a.student_name || '');
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [payments, sortBy]);

  // useCallback ile optimize edilmiş fonksiyonlar
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }, []);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);



  const exportToExcel = useCallback(() => {
    try {
      // Excel için veri hazırlama
      const excelData = sortedPayments.map(payment => ({
        'Tarih': formatDate(payment.tarih),
        'Saat': new Date(payment.tarih).toLocaleTimeString('tr-TR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        'Öğrenci Adı': payment.student_name || '-',
        'Öğrenci Email': payment.student_email || '-',
        'Veli Adı': payment.parent_name || '-',
        'Veli Telefon': payment.parent_phone || '-',
        'Banka': payment.banka_ismi || 'Belirtilmemiş',
        'Tutar (TL)': payment.tutar || 0
      }));

      // Özet bilgileri ekle
      const summaryData = [
        { 'Tarih': '', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': '' },
        { 'Tarih': 'ÖZET BİLGİLER', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': '' },
        { 'Tarih': 'Toplam Ödeme Sayısı:', 'Saat': sortedPayments.length, 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': '' },
        { 'Tarih': 'Toplam Tutar:', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': sortedPayments.reduce((sum, p) => sum + parseFloat(p.tutar || 0), 0) },
        { 'Tarih': 'Ortalama Tutar:', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': sortedPayments.length > 0 ? (sortedPayments.reduce((sum, p) => sum + parseFloat(p.tutar || 0), 0) / sortedPayments.length).toFixed(2) : 0 }
      ];

      // Tüm veriyi birleştir
      const finalData = [...excelData, ...summaryData];

      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(finalData);

      // Sütun genişliklerini ayarla
      const colWidths = [
        { wch: 12 }, // Tarih
        { wch: 10 }, // Saat
        { wch: 25 }, // Öğrenci Adı
        { wch: 30 }, // Öğrenci Email
        { wch: 25 }, // Veli Adı
        { wch: 15 }, // Veli Telefon
        { wch: 20 }, // Banka
        { wch: 15 }  // Tutar
      ];
      ws['!cols'] = colWidths;

      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Gelen Ödemeler');

      // Dosya adını oluştur
      const date = new Date();
      const fileName = `Gelen_Odemeler_${date.getFullYear()}_${String(date.getMonth() + 1).padStart(2, '0')}_${String(date.getDate()).padStart(2, '0')}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(wb, fileName);

      // Başarı mesajı (opsiyonel)
      console.log('Excel dosyası başarıyla indirildi:', fileName);
    } catch (error) {
      console.error('Excel export hatası:', error);
      alert('Excel dosyası oluşturulurken hata oluştu!');
    }
  }, [sortedPayments, formatDate]);

  const printReport = useCallback(() => {
    window.print();
  }, []);

  // Ödeme satırı componenti - React.memo ile optimize edilmiş
  const PaymentRow = React.memo(({ payment }) => (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="py-3 px-4">
        <div>
          <p className="font-medium text-gray-800">{formatDate(payment.tarih)}</p>
          <p className="text-sm text-gray-500">
            {new Date(payment.tarih).toLocaleTimeString('tr-TR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </p>
        </div>
      </td>
      <td className="py-3 px-4">
        <div>
          <p className="font-medium">{payment.student_name}</p>
          <p className="text-sm text-gray-500">{payment.student_email}</p>
        </div>
      </td>
      <td className="py-3 px-4">
        <div>
          <p className="text-sm text-gray-800">{payment.parent_name || '-'}</p>
          <p className="text-sm text-gray-500">{payment.parent_phone || '-'}</p>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
          {payment.banka_ismi || 'Belirtilmemiş'}
        </span>
      </td>
      <td className="text-right py-3 px-4">
        <p className="font-semibold text-green-600 text-lg">
          {formatCurrency(payment.tutar)}
        </p>
      </td>
    </tr>
  ), (prevProps, nextProps) => prevProps.payment.id === nextProps.payment.id);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Gelen Ödemeler</h1>
              <p className="text-gray-600 mt-1">Öğrenci ödemelerinin detaylı raporu</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={printReport}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Yazdır
              </button>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel İndir
              </button>
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dönem</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                disabled={loading}
              >
                <option value="today">Bugün</option>
                <option value="thisWeek">Bu Hafta</option>
                <option value="thisMonth">Bu Ay</option>
                <option value="thisYear">Bu Yıl</option>
                <option value="all">Tümü</option>
                <option value="custom">Özel Tarih</option>
              </select>
            </div>

            {selectedPeriod === 'thisMonth' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yıl</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    disabled={loading}
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ay</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    disabled={loading}
                  >
                    {['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'].map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {selectedPeriod === 'thisYear' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yıl</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  disabled={loading}
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedPeriod === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Başlangıç</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={selectedDateRange.start}
                    onChange={(e) => setSelectedDateRange(prev => ({ ...prev, start: e.target.value }))}
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bitiş</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={selectedDateRange.end}
                    onChange={(e) => setSelectedDateRange(prev => ({ ...prev, end: e.target.value }))}
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards - Sadece Süper Admin */}
        {isSuperAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Toplam Tahsilat</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(stats.total_amount)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ödeme Sayısı</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.payment_count}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Ortalama Ödeme</p>
                  <p className="text-lg font-bold text-purple-600">{formatCurrency(stats.average_amount)}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bugün</p>
                  <p className="text-lg font-bold text-orange-600">{formatCurrency(stats.today_amount)}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bu Hafta</p>
                  <p className="text-lg font-bold text-indigo-600">{formatCurrency(stats.week_amount)}</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bu Ay</p>
                  <p className="text-lg font-bold text-teal-600">{formatCurrency(stats.month_amount)}</p>
                </div>
                <div className="p-3 bg-teal-100 rounded-lg">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Öğrenci Ara</label>
              <input
                type="text"
                placeholder="Ad, veli, telefon..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Banka (Arama yapabilirsiniz)
              </label>
              <SearchableSelect
                options={bankList}
                value={bankFilter}
                onChange={(value) => setBankFilter(value)}
                placeholder="Banka seçin veya yazın..."
                disabled={loading}
                showAllOption={true}
                allOptionLabel="Tüm Bankalar"
                allOptionValue="all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sıralama</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                disabled={loading}
              >
                <option value="date_desc">En Yeni</option>
                <option value="date_asc">En Eski</option>
                <option value="amount_desc">En Yüksek Tutar</option>
                <option value="amount_asc">En Düşük Tutar</option>
                <option value="name_asc">A-Z İsim</option>
                <option value="name_desc">Z-A İsim</option>
              </select>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            </div>
          ) : sortedPayments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Tarih</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Öğrenci</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Veli</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Banka</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Tutar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedPayments.map((payment) => (
                    <PaymentRow key={payment.id} payment={payment} />
                  ))}
                </tbody>
                {isSuperAdmin && (
                  <tfoot className="border-t-2 border-gray-300 bg-gray-50">
                    <tr>
                      <td colSpan="4" className="py-4 px-4 font-bold text-gray-700">
                        TOPLAM ({sortedPayments.length} ödeme)
                      </td>
                      <td className="text-right py-4 px-4 font-bold text-green-700 text-lg">
                        {formatCurrency(sortedPayments.reduce((sum, p) => sum + parseFloat(p.tutar || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p>Bu dönemde ödeme kaydı bulunamadı</p>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default GelenOdemeler;
